import { staticBreadcrumbLabelForPath } from "@/lib/breadcrumbRouteLabels";
import { stripHubTrailFromSearch } from "@/lib/navigationTrail";

/** Query parameters that identify a workspace. Everything else is deny-by-default. */
export const HUB_PRESENCE_ALLOWED_QUERY_PARAMS = [
  "batch_id",
  "batch_item_id",
  "highlight_batch_id",
  "batch",
  "serial",
  "episode",
  "sales_order_id",
  "shipment_id",
  "mv_document_id",
  "sku",
  "location_id",
  "location",
  "request",
  "ticket",
  "purchase_request",
  "packing_quantity_request",
  "transfer_order_id",
  "transfer",
  "label",
  "po",
  "cpn",
  "fs_account_id",
  "station",
  "machine",
  "id",
  "number",
  "source",
  "topic",
] as const;

type AllowedQueryParam = (typeof HUB_PRESENCE_ALLOWED_QUERY_PARAMS)[number];
const MAX_PRESENCE_NAVIGABLE_PATH_LENGTH = 300;

const ENTITY_TYPE_BY_PARAM: Readonly<Record<AllowedQueryParam, string>> = {
  batch_id: "batch",
  batch_item_id: "batch_item",
  highlight_batch_id: "batch",
  batch: "batch",
  serial: "device",
  episode: "device_episode",
  sales_order_id: "sales_order",
  shipment_id: "shipment",
  mv_document_id: "gosi",
  sku: "sku",
  location_id: "location",
  location: "location",
  request: "request",
  ticket: "ticket",
  purchase_request: "purchase_request",
  packing_quantity_request: "packing_quantity_request",
  transfer_order_id: "transfer_order",
  transfer: "transfer",
  label: "device",
  po: "purchase_order",
  cpn: "component",
  fs_account_id: "account",
  station: "station",
  machine: "machine",
  id: "record",
  number: "work_order",
  source: "notification_source",
  topic: "notification_topic",
};

/** Longer path prefixes first so `/manufacturing-management` wins over `/manufacturing`. */
const MODULE_PATH_PREFIXES: ReadonlyArray<{ prefix: string; moduleKey: string | null }> = [
  { prefix: "/manufacturing-management", moduleKey: "manufacturing_management" },
  { prefix: "/manufacturing", moduleKey: "manufacturing" },
  { prefix: "/clamp-qc", moduleKey: "clampqc" },
  { prefix: "/inside-sales", moduleKey: "inside_sales" },
  { prefix: "/personal", moduleKey: "personal" },
  { prefix: "/tablet-pin", moduleKey: null },
  { prefix: "/settings", moduleKey: null },
  { prefix: "/notifications", moduleKey: null },
  { prefix: "/floor", moduleKey: null },
  { prefix: "/access-needed", moduleKey: null },
  { prefix: "/integrations", moduleKey: null },
  { prefix: "/users", moduleKey: "admin" },
  { prefix: "/mrp", moduleKey: "mrp" },
  { prefix: "/admin", moduleKey: "admin" },
  { prefix: "/shipping", moduleKey: "shipping" },
  { prefix: "/documents", moduleKey: "documents" },
  { prefix: "/accounting", moduleKey: "accounting" },
  { prefix: "/quality", moduleKey: "quality" },
  { prefix: "/marketing", moduleKey: "marketing" },
  { prefix: "/wireless", moduleKey: "wireless" },
  { prefix: "/engineering", moduleKey: "engineering" },
  { prefix: "/erp", moduleKey: "erp" },
  { prefix: "/bom", moduleKey: "bom" },
  { prefix: "/wiki", moduleKey: "wiki" },
  { prefix: "/misc", moduleKey: "personal" },
  { prefix: "/warehouse", moduleKey: "warehouse" },
  { prefix: "/commissions", moduleKey: "commissions" },
  { prefix: "/art-and-labels", moduleKey: "art_and_labels" },
  { prefix: "/printing", moduleKey: "printing" },
  /** Carton scan short links (`/i/<box hex>`, `/inv/...`) render inside the warehouse layout. */
  { prefix: "/inv", moduleKey: "warehouse" },
  { prefix: "/i", moduleKey: "warehouse" },
  /** Arrival label short links (`/a/<package hex>`) render inside shipping. */
  { prefix: "/a", moduleKey: "shipping" },
];

export type HubPresenceRoute = {
  routeKey: string;
  pathname: string;
  pageLabel: string;
  moduleKey: string | null;
  entityType: string | null;
  entityId: string | null;
  navigablePath: string;
};

function normalizePathname(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname.length > 0 ? pathname : "/";
}

function parseSearchParams(search: string): URLSearchParams {
  const normalized = search.startsWith("?") ? search.slice(1) : search;
  return new URLSearchParams(normalized);
}

/** Path-param workspaces that should publish entity presence (list pages group by these). */
const PATH_ENTITY_PATTERNS: ReadonlyArray<{ test: RegExp; entityType: string }> = [
  { test: /^\/accounting\/suntronic-invoices\/([^/]+)$/u, entityType: "manufacturer_invoice" },
];

export function entityFromPathname(pathname: string): { entityType: string; entityId: string } | null {
  const normalized = normalizePathname(pathname);
  for (const entry of PATH_ENTITY_PATTERNS) {
    const match = entry.test.exec(normalized);
    const entityId = match?.[1]?.trim() ?? "";
    if (entityId !== "") {
      return { entityType: entry.entityType, entityId };
    }
  }
  return null;
}

function collectAllowedQueryEntries(search: string): Array<[AllowedQueryParam, string]> {
  const params = parseSearchParams(stripHubTrailFromSearch(search));
  const out: Array<[AllowedQueryParam, string]> = [];
  for (const key of HUB_PRESENCE_ALLOWED_QUERY_PARAMS) {
    const raw = params.get(key);
    if (raw == null) continue;
    const trimmed = raw.trim();
    if (trimmed === "" || trimmed.length > 120) continue;
    out.push([key, trimmed]);
  }
  return out;
}

function buildQuerySuffix(entries: Array<[AllowedQueryParam, string]>): string {
  if (entries.length === 0) return "";
  const params = new URLSearchParams();
  for (const [key, value] of entries) {
    params.set(key, value);
  }
  return `?${params.toString()}`;
}

export function resolveHubPresenceModuleKey(pathname: string): string | null {
  const normalized = normalizePathname(pathname);
  for (const entry of MODULE_PATH_PREFIXES) {
    if (normalized === entry.prefix || normalized.startsWith(`${entry.prefix}/`)) {
      return entry.moduleKey;
    }
  }
  return null;
}

/**
 * Normalize the current location into a presence route payload.
 * Query parameters are deny-by-default; only the allowlist may appear on routeKey / navigablePath.
 */
export function resolveHubPresenceRoute(pathname: string, search: string): HubPresenceRoute {
  const normalizedPath = normalizePathname(pathname);
  const allowed = collectAllowedQueryEntries(search);
  let querySuffix = buildQuerySuffix(allowed);
  while (
    allowed.length > 0 &&
    normalizedPath.length + querySuffix.length > MAX_PRESENCE_NAVIGABLE_PATH_LENGTH
  ) {
    allowed.pop();
    querySuffix = buildQuerySuffix(allowed);
  }
  const navigablePath = `${normalizedPath}${querySuffix}`;
  const routeKey = navigablePath;

  let entityType: string | null = null;
  let entityId: string | null = null;
  if (allowed.length > 0) {
    const [param, value] = allowed[0]!;
    entityType = ENTITY_TYPE_BY_PARAM[param];
    entityId = value;
  } else {
    const fromPath = entityFromPathname(normalizedPath);
    if (fromPath != null) {
      entityType = fromPath.entityType;
      entityId = fromPath.entityId;
    }
  }

  return {
    routeKey,
    pathname: normalizedPath,
    pageLabel: staticBreadcrumbLabelForPath(normalizedPath),
    moduleKey: resolveHubPresenceModuleKey(normalizedPath),
    entityType,
    entityId,
    navigablePath,
  };
}
